import { WarningAmber } from '@mui/icons-material';
import {
  AlertTitle,
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { PricingSheetState } from '../../../../../shared/types';
import GenericContainer from '../../../GenericContainer';
import { colors } from '../../../../../shared/constants';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import isPricingSheetExpiring from '../../../../../shared/helpers/isPricingSheetExpiring';
import { PricingSheetTableData } from '../../../../../shared/types/pricingSheets';
import useUserContext from '../../../providers/User/UserProvider';
import { isEditingRights } from '../../../../../shared/helpers/isEditingRights';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DefaultTooltip from '../../../components/Tooltips/DefaultTooltip';
import { isResponsiblePerson } from '../../../../../shared/helpers/isResponsiblePerson';

export const PricingSheetsSummaryTable: React.FC<{
  pricingSheets: PricingSheetTableData[];
  onStateFilterClick: (state: PricingSheetState[]) => void;
  onExpiringFilterClick: () => void;
  onArchiveFilterClick: () => void;
  onSharedFilterClick: () => void;
}> = ({
  pricingSheets,
  onStateFilterClick,
  onExpiringFilterClick,
  onArchiveFilterClick,
  onSharedFilterClick,
}) => {
  const { userEmail } = useUserContext();
  if (!userEmail) {
    return <></>;
  }

  const ownSheets = pricingSheets.filter(
    (sheet) =>
      isResponsiblePerson(sheet, userEmail) ||
      isEditingRights(sheet.editingRights, userEmail),
  );

  const unArchivedOwnSheets = pricingSheets.filter(
    (sheet) =>
      (isResponsiblePerson(sheet, userEmail) ||
        isEditingRights(sheet.editingRights, userEmail)) &&
      !sheet.archivedAt,
  );

  /** IN USE */
  const inUse = unArchivedOwnSheets.filter(
    (sheet) => sheet.state === PricingSheetState.InUse,
  );
  const inUseExpired = inUse.filter((sheet) => {
    return isPricingSheetExpiring(sheet);
  });

  /** INCOMPLETE */
  const incomplete = unArchivedOwnSheets.filter(
    (sheet) =>
      sheet.state === PricingSheetState.OkDraft ||
      sheet.state === PricingSheetState.WarningDraft ||
      sheet.state === PricingSheetState.Draft ||
      sheet.state === PricingSheetState.Pending ||
      sheet.state === PricingSheetState.Ready ||
      sheet.state === PricingSheetState.Approved,
  );
  const approved = incomplete.filter(
    (sheet) => sheet.state === PricingSheetState.Approved,
  );
  const waitingForApproval = incomplete.filter(
    (sheet) => sheet.state === PricingSheetState.Pending,
  );
  const waitingForExport = incomplete.filter(
    (sheet) => sheet.state === PricingSheetState.Ready,
  );
  const draft = incomplete.filter(
    (sheet) =>
      sheet.state === PricingSheetState.Draft ||
      sheet.state === PricingSheetState.OkDraft ||
      sheet.state === PricingSheetState.WarningDraft,
  );

  /** ARCHIVED */
  const archived = pricingSheets.filter((sheet) =>
    Boolean(sheet.archivedAt),
  );
  /** SHARED */
  const sharedToUser = pricingSheets.filter((sheet) =>
    sheet?.editingRights?.includes(userEmail),
  );
  /** WAITING FOR USER'S APPROVAL */
  //  const waitingForUsersApproval = sheet

  const renderCellValues = (sheets: PricingSheetTableData[]) => {
    const sharedCount = sheets.filter((sheet) =>
      isEditingRights(sheet.editingRights, userEmail),
    ).length;
    return ` ${sheets.filter((sheet) => isResponsiblePerson(sheet, userEmail)).length} 
    ${sharedCount > 0 ? `(${sharedCount})` : ''}`;
  };
  return (
    <Box sx={{ mt: -8 }}>
      <GenericContainer
        borderColor={colors.ramiGreen}
        border
        legendTitle="Yhteenveto"
        legendIcon={
          <ArticleOutlinedIcon
            sx={{ fontSize: 20, color: 'text.secondary' }}
          />
        }
      >
        <AlertTitle sx={{ fontSize: 20 }}>
          <Box display={'flex'} justifyContent={'space-between'}>
            <Typography>Omat hinnastot</Typography>
            <DefaultTooltip title="Taulukosta näet yhteenvedon hinnastoista, joista olet vastuussa. Suluissa oleva luku tarkoittaa sinulle jaettujen hinnastojen määrää.">
              <HelpOutlineIcon
                sx={{
                  fontSize: 20,
                  color: 'text.secondary',
                  cursor: 'help',
                }}
              />
            </DefaultTooltip>
          </Box>
        </AlertTitle>
        <Box sx={{ width: 300 }}>
          <Table size="small">
            <TableBody>
              <TableRow sx={{ borderBottom: 'none' }}>
                <TableCell
                  sx={{
                    borderBottom: 'none',

                    fontWeight: 'bold',
                  }}
                >
                  Käytössä
                </TableCell>
                <TableCell
                  sx={{
                    borderBottom: 'none',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    color: 'blue',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                  onClick={() => {
                    onStateFilterClick([PricingSheetState.InUse]);
                  }}
                >
                  {renderCellValues(inUse)}
                </TableCell>
              </TableRow>
              <TableRow sx={{ borderBottom: 'none' }}>
                <TableCell
                  sx={{
                    borderBottom: 'none',
                    paddingLeft: 4,
                  }}
                >
                  Vanhentuvat
                </TableCell>
                <TableCell
                  onClick={onExpiringFilterClick}
                  sx={{
                    borderBottom: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: 'pointer',
                    color: 'blue',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {renderCellValues(inUseExpired)}
                  {inUseExpired.length > 0 && (
                    <WarningAmber
                      sx={{ fontSize: 20 }}
                      color="warning"
                    />
                  )}
                </TableCell>
              </TableRow>
              <TableRow sx={{ borderBottom: 'none' }}>
                <TableCell
                  sx={{
                    borderBottom: 'none',
                    fontWeight: 'bold',
                  }}
                >
                  Keskeneräiset
                </TableCell>
                <TableCell
                  sx={{
                    borderBottom: 'none',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    color: 'blue',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                  onClick={() => {
                    onStateFilterClick([
                      PricingSheetState.OkDraft,
                      PricingSheetState.WarningDraft,
                      PricingSheetState.Draft,
                      PricingSheetState.Pending,
                      PricingSheetState.Approved,
                      PricingSheetState.Ready,
                    ]);
                  }}
                >
                  {renderCellValues(incomplete)}
                </TableCell>
              </TableRow>
              <TableRow sx={{ borderBottom: 'none' }}>
                <TableCell
                  sx={{
                    borderBottom: 'none',
                    paddingLeft: 4,
                  }}
                >
                  Luonnos
                </TableCell>
                <TableCell
                  sx={{
                    borderBottom: 'none',
                    cursor: 'pointer',
                    color: 'blue',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                  onClick={() => {
                    onStateFilterClick([
                      PricingSheetState.OkDraft,
                      PricingSheetState.WarningDraft,
                      PricingSheetState.Draft,
                    ]);
                  }}
                >
                  {renderCellValues(draft)}
                </TableCell>
              </TableRow>
              <TableRow sx={{ borderBottom: 'none' }}>
                <TableCell
                  sx={{
                    borderBottom: 'none',
                    paddingLeft: 4,
                  }}
                >
                  Odottaa hyväksyntää
                </TableCell>
                <TableCell
                  sx={{
                    borderBottom: 'none',
                    cursor: 'pointer',
                    color: 'blue',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                  onClick={() => {
                    onStateFilterClick([PricingSheetState.Pending]);
                  }}
                >
                  {renderCellValues(waitingForApproval)}
                </TableCell>
              </TableRow>
              <TableRow sx={{ borderBottom: 'none' }}>
                <TableCell
                  sx={{
                    borderBottom: 'none',
                    paddingLeft: 4,
                  }}
                >
                  Hyväksytyt
                </TableCell>
                <TableCell
                  onClick={() => {
                    onStateFilterClick([PricingSheetState.Approved]);
                  }}
                  sx={{
                    borderBottom: 'none',
                    cursor: 'pointer',
                    color: 'blue',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {renderCellValues(approved)}
                </TableCell>
              </TableRow>

              <TableRow sx={{ borderBottom: 'none' }}>
                <TableCell
                  sx={{
                    borderBottom: 'none',
                    paddingLeft: 4,
                  }}
                >
                  Odottaa vientiä
                </TableCell>
                <TableCell
                  sx={{
                    borderBottom: 'none',
                    cursor: 'pointer',
                    color: 'blue',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                  onClick={() => {
                    onStateFilterClick([PricingSheetState.Ready]);
                  }}
                >
                  {renderCellValues(waitingForExport)}
                </TableCell>
              </TableRow>

              <TableRow sx={{ borderBottom: 'none' }}>
                <TableCell
                  sx={{
                    borderBottom: 'none',
                    fontWeight: 'bold',
                  }}
                >
                  Arkistoidut
                </TableCell>
                <TableCell
                  onClick={onArchiveFilterClick}
                  sx={{
                    borderBottom: 'none',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    color: 'blue',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {renderCellValues(archived)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  Minulle jaetut
                </TableCell>
                <TableCell
                  onClick={onSharedFilterClick}
                  sx={{
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    color: 'blue',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {sharedToUser.length}
                </TableCell>
              </TableRow>

              <TableRow sx={{ borderBottom: 'none' }}>
                <TableCell
                  sx={{
                    borderBottom: 'none',
                    fontWeight: 'bold',
                  }}
                >
                  Yhteensä
                </TableCell>
                <TableCell
                  onClick={onSharedFilterClick}
                  sx={{
                    borderBottom: 'none',
                    fontWeight: 'bold',
                  }}
                >
                  {ownSheets.length}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </GenericContainer>
    </Box>
  );
};
